import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPages, createPage, updatePage, deletePage, duplicatePage } from './pageSlice';
import PageBuilder from '../../components/pageBuilder/PageBuilder';
import Shell from '../dashboard/Shell';

const Pages = () => {
  const dispatch = useDispatch();
  const { pages, loading, error } = useSelector(state => state.pages || {});
  const token = useSelector(state => state.auth.token);
  
  const [showPageBuilder, setShowPageBuilder] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [showPageForm, setShowPageForm] = useState(false);
  const [pageFormData, setPageFormData] = useState({
    title: '',
    slug: '',
    description: '',
    theme: 'modern'
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });

  useEffect(() => {
    if (token) {
      dispatch(fetchPages({ token, params: filters }));
    }
  }, [dispatch, token, filters]);

  const handleCreatePage = () => {
    setEditingPage(null);
    setPageFormData({
      title: '',
      slug: '',
      description: '',
      theme: 'modern'
    });
    setShowPageForm(true);
  };

  const handleEditPage = (page) => {
    setEditingPage(page);
    setShowPageBuilder(true);
  };

  const handlePageFormChange = (field, value) => {
    setPageFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-generate slug from title
    if (field === 'title') {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setPageFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handlePageFormSubmit = (e) => {
    e.preventDefault();
    if (!pageFormData.title.trim()) {
      alert('Please enter a page title');
      return;
    }
    
    setEditingPage({
      ...pageFormData,
      content: []
    });
    setShowPageForm(false);
    setShowPageBuilder(true);
  };

  const handleCancelPageForm = () => {
    setShowPageForm(false);
    setPageFormData({
      title: '',
      slug: '',
      description: '',
      theme: 'modern'
    });
  };

  const handleSavePage = (blocks) => {
    const pageData = {
      title: editingPage?.title || 'New Page',
      slug: editingPage?.slug || `page-${Date.now()}`,
      description: editingPage?.description || '',
      content: blocks, // Changed from 'blocks' to 'content' to match backend schema
      status: editingPage?.status || 'draft',
      theme: editingPage?.theme || 'modern'
    };

    if (editingPage && editingPage._id && editingPage._id !== 'undefined') {
      dispatch(updatePage({ pageId: editingPage._id, pageData, token }));
    } else {
      dispatch(createPage({ pageData, token }));
    }
    
    setShowPageBuilder(false);
    setEditingPage(null);
  };

  const handleCancelPage = () => {
    setShowPageBuilder(false);
    setEditingPage(null);
  };

  const handleDeletePage = (pageId) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      dispatch(deletePage({ pageId, token }));
    }
  };

  const handleDuplicatePage = (pageId) => {
    dispatch(duplicatePage({ pageId, token }));
  };

  const handleStatusChange = (pageId, newStatus) => {
    dispatch(updatePage({ 
      pageId, 
      pageData: { status: newStatus }, 
      token 
    }));
  };

  const filteredPages = pages?.filter(page => {
    if (filters.status !== 'all' && page.status !== filters.status) return false;
    if (filters.search && !page.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  }) || [];

  if (showPageForm) {
    return (
      <Shell>
        <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '2rem', color: '#1f2937' }}>
            Create New Page
          </h1>
          
          <form onSubmit={handlePageFormSubmit} style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500',
                color: '#374151'
              }}>
                Page Title *
              </label>
              <input
                type="text"
                value={pageFormData.title}
                onChange={(e) => handlePageFormChange('title', e.target.value)}
                placeholder="Enter page title"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
                required
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500',
                color: '#374151'
              }}>
                Page URL (Slug)
              </label>
              <input
                type="text"
                value={pageFormData.slug}
                onChange={(e) => handlePageFormChange('slug', e.target.value)}
                placeholder="page-url"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                This will be the URL for your page: /pages/{pageFormData.slug}
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500',
                color: '#374151'
              }}>
                Description
              </label>
              <textarea
                value={pageFormData.description}
                onChange={(e) => handlePageFormChange('description', e.target.value)}
                placeholder="Enter page description (optional)"
                rows="3"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.25rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500',
                color: '#374151'
              }}>
                Theme
              </label>
              <select
                value={pageFormData.theme}
                onChange={(e) => handlePageFormChange('theme', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value="modern">Modern</option>
                <option value="elegant">Elegant</option>
                <option value="minimal">Minimal</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                style={{
                  flex: 1,
                  backgroundColor: '#2563eb',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Create Page
              </button>
              <button
                type="button"
                onClick={handleCancelPageForm}
                style={{
                  flex: 1,
                  backgroundColor: '#6b7280',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </Shell>
    );
  }

  if (showPageBuilder) {
    return (
      <PageBuilder
        initialBlocks={editingPage?.content || editingPage?.blocks || []}
        onSave={handleSavePage}
        onCancel={handleCancelPage}
      />
    );
  }

  return (
    <Shell>
      <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            color: '#1f2937'
          }}>
            Page Management
          </h1>
          <p style={{ color: '#6b7280' }}>
            Create and manage your custom pages
          </p>
        </div>
        <button
          onClick={handleCreatePage}
          style={{
            backgroundColor: '#2563eb',
            color: 'white',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#1d4ed8';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#2563eb';
          }}
        >
          + Create New Page
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        <div>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            color: '#374151'
          }}>
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            style={{
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.25rem',
              fontSize: '1rem'
            }}
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '300px' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            color: '#374151'
          }}>
            Search Pages
          </label>
          <input
            type="text"
            placeholder="Search by page title..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.25rem',
              fontSize: '1rem'
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          border: '1px solid #fca5a5'
        }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '4rem 0'
        }}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>
            Loading pages...
          </p>
        </div>
      )}

      {/* Pages Grid */}
      {!loading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredPages.map((page) => (
            <div
              key={page._id}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            >
              {/* Page Header */}
              <div style={{
                padding: '1rem',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.5rem'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: 0,
                    flex: 1
                  }}>
                    {page.title}
                  </h3>
                  <div style={{
                    display: 'flex',
                    gap: '0.25rem'
                  }}>
                    <button
                      onClick={() => handleDuplicatePage(page._id)}
                      style={{
                        padding: '0.25rem',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                      title="Duplicate"
                    >
                      📋
                    </button>
                    <button
                      onClick={() => handleDeletePage(page._id)}
                      style={{
                        padding: '0.25rem',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    backgroundColor: page.status === 'published' ? '#d1fae5' : 
                                   page.status === 'draft' ? '#fef3c7' : '#f3f4f6',
                    color: page.status === 'published' ? '#065f46' : 
                          page.status === 'draft' ? '#92400e' : '#374151',
                    fontWeight: '500'
                  }}>
                    {page.status}
                  </span>
                  {page.isHomePage && (
                    <span style={{
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      fontWeight: '500'
                    }}>
                      Home
                    </span>
                  )}
                </div>
                
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  margin: 0
                }}>
                  /{page.slug}
                </p>
              </div>

              {/* Page Content */}
              <div style={{ padding: '1rem' }}>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  marginBottom: '1rem',
                  lineHeight: '1.5'
                }}>
                  {page.description || 'No description provided'}
                </p>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#6b7280'
                  }}>
                    {page.blocks?.length || 0} block(s)
                  </span>
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#6b7280'
                  }}>
                    {page.theme}
                  </span>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <button
                    onClick={() => handleEditPage(page)}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#1d4ed8';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#2563eb';
                    }}
                  >
                    Edit
                  </button>
                  
                  <select
                    value={page.status}
                    onChange={(e) => handleStatusChange(page._id, e.target.value)}
                    style={{
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.25rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Pages Found */}
      {!loading && filteredPages.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          color: '#6b7280'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '0.5rem',
            color: '#374151'
          }}>
            No Pages Found
          </h3>
          <p style={{ marginBottom: '2rem' }}>
            {filters.search || filters.status !== 'all' 
              ? 'Try adjusting your filters or search terms'
              : 'Create your first custom page to get started'
            }
          </p>
          {(!filters.search && filters.status === 'all') && (
            <button
              onClick={handleCreatePage}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                padding: '0.75rem 1.5rem',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Create Your First Page
            </button>
          )}
        </div>
      )}

      {/* Add CSS for loading animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </Shell>
  );
};

export default Pages;
